import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Undo2, X } from "lucide-react";
import { Button } from "../../components/Button";
import { useFocusTrap, useGlobalKeydown, useRestoreFocus } from "../../lib/keyboard";
import { api } from "../../api/client";
import { RevisionList } from "./RevisionList";
import { DiffView } from "./DiffView";
import { RestoreConfirmDialog } from "./RestoreConfirmDialog";
import type { RestoreTarget } from "./RestoreConfirmDialog";
import type { QuoteDetail, QuoteDiff, RevisionSummary } from "../../api/types";

// 版本紀錄側板:上半是版本清單,下半是選定版本與最新版之間的變更清單。
//
// 比較的終點固定是最新的那一版,起點由使用者在清單上點選,預設是次新的那一版 ——
// 一打開就看得到「最近這次存檔改了什麼」,那是業主最常問的問題。
//
// 差異與增減值全部由後端算好,這裡一格都不自己算:金額在瀏覽器是浮點數,
// 前端一旦動手減,遲早會有一張報價單上出現 0.30000000000000004。

export interface RevisionPanelProps {
  open: boolean;
  quoteId: number;
  /** 報價單目前的版本號,用來在清單上標出「目前版本」。 */
  currentVersion: number;
  onClose: () => void;
  /** 復原成功。回傳的是後端重算後的完整報價單(而且已經是新的一版)。 */
  onRestored: (quote: QuoteDetail) => void;
  /**
   * 這張單已封存。**清單與差異照樣看得到,三種復原一律按不動**(規格 §4.1)。
   *
   * 復原走的是與存檔完全相同的寫入路徑,後端的 `POST /quotes/{id}/restore` 會回
   * 409;這裡擋的是「按了才知道」—— 而且真的讓它進去,改動就存進一張在列表、
   * 日曆、搜尋、漏斗四處都看不到的單裡,還會發一則收件人找不到那張單的通知。
   *
   * 按不動的鈕**不藏起來**:藏起來只會讓人以為版本紀錄壞了。
   */
  archived?: boolean;
  /**
   * **這個角色**動不了這張單時的那一句(財務唯讀,設計規格 §2.1)。
   *
   * 與 `archived` 是兩個不同的理由,所以是兩個 prop:一個是「這張單現在的狀態」,
   * 一個是「你是誰」。復原走的是與存檔完全相同的寫入路徑(後端同樣是
   * `require_business`),而且後果更大 —— 它整份覆蓋。存檔鈕對財務已經是
   * disabled 且說得出原因,這三顆沒有理由是亮的。
   *
   * 兩個同時成立時**先講角色**(見 `blockedReason`),與 `QuoteEditor` 的
   * `writeBlockedReason` 同一個優先序。
   */
  readOnlyReason?: string;
}

/** 已封存時,三顆復原鈕滑過去看到的那一句。面板上另有一行同樣意思的文字。 */
const ARCHIVED_REASON = "這張報價單已封存,要復原版本請先把它從封存區撈回來";

function messageOf(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function RevisionPanel({
  open,
  quoteId,
  currentVersion,
  onClose,
  onRestored,
  archived = false,
  readOnlyReason,
}: RevisionPanelProps) {
  const panelRef = useRef<HTMLElement>(null);

  /**
   * 三顆復原按不動的理由。`undefined` 就是按得動。
   *
   * **角色排在封存前面**:財務就算把單從封存區撈回來也還是改不動,
   * 叫他「先復原」只是讓他去做一件做不到、而且做到了也沒用的事
   * (與 `QuoteEditor.writeBlockedReason` 同一個優先序)。
   */
  const blockedReason = readOnlyReason ?? (archived ? ARCHIVED_REASON : undefined);
  const blocked = blockedReason !== undefined;

  const [revisions, setRevisions] = useState<RevisionSummary[]>([]);
  const [listLoading, setListLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);
  const [fromVersion, setFromVersion] = useState<number | null>(null);

  const [diff, setDiff] = useState<QuoteDiff | null>(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [diffError, setDiffError] = useState<string | null>(null);

  const [target, setTarget] = useState<RestoreTarget | null>(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreError, setRestoreError] = useState<string | null>(null);

  // 復原完要把版本清單重讀一次:剛剛那次復原自己也是一個新版本,清單上要看得到。
  const [reloadToken, setReloadToken] = useState(0);

  // 比較的終點永遠是最新的那一版。
  const toVersion = revisions.length > 0 ? revisions[0].version : null;

  // 確認對話框開著時把側板的焦點陷阱讓開,否則兩個陷阱會互搶 Tab。
  useFocusTrap(panelRef, open && target === null);
  useRestoreFocus(open);
  useGlobalKeydown((event) => {
    if (event.key === "Escape") onClose();
  }, open && target === null);

  // 側板開著時鎖住背景捲動,免得滾側板卻是整頁在動。
  useEffect(() => {
    if (!open || typeof document === "undefined") return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);

  // 版本清單。每次開啟重讀一次 —— 別人可能在這期間存過檔。
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setListLoading(true);
    setListError(null);
    api
      .get<RevisionSummary[]>(`/quotes/${quoteId}/revisions`)
      .then((rows) => {
        if (cancelled) return;
        const list = Array.isArray(rows) ? rows : [];
        setRevisions(list);
        // 預設比較最新的兩個版本。只有一版時沒有可比的對象。
        setFromVersion(list.length >= 2 ? list[1].version : null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setRevisions([]);
        setFromVersion(null);
        setListError(messageOf(error, "版本紀錄載入失敗,請稍後再試"));
      })
      .finally(() => {
        if (!cancelled) setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, quoteId, reloadToken]);

  // 選定的兩版之間的差異。同一版跟自己比沒有意義,不送這一趟。
  useEffect(() => {
    if (!open || fromVersion === null || toVersion === null || fromVersion === toVersion) {
      setDiff(null);
      setDiffError(null);
      return;
    }
    let cancelled = false;
    setDiffLoading(true);
    setDiffError(null);
    api
      .get<QuoteDiff>(`/quotes/${quoteId}/diff`, { from: fromVersion, to: toVersion })
      .then((result) => {
        if (!cancelled) setDiff(result);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setDiff(null);
        setDiffError(messageOf(error, "差異載入失敗,請稍後再試"));
      })
      .finally(() => {
        if (!cancelled) setDiffLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, quoteId, fromVersion, toVersion]);

  // 換一個比較起點時,先把上一次沒送出的確認收掉,免得對話框裡寫的版本號
  // 與清單上選的對不起來。
  const selectFrom = useCallback((version: number) => {
    setFromVersion(version);
    setTarget(null);
    setRestoreError(null);
  }, []);

  const askRestore = useCallback(
    (next: RestoreTarget) => {
      // 三顆鈕都已經是 disabled,這裡再擋一次:disabled 只是畫面上的樣子,
      // 而這一支是唯一通往「送出 /restore」的門。
      if (archived) return;
      setTarget(next);
      setRestoreError(null);
    },
    [archived],
  );

  const confirmRestore = async () => {
    if (target === null || fromVersion === null) return;
    setRestoring(true);
    setRestoreError(null);
    try {
      const saved = await api.post<QuoteDetail>(`/quotes/${quoteId}/restore`, {
        from_version: fromVersion,
        scope: target.scope,
        uid: target.uid,
      });
      onRestored(saved);
      setTarget(null);
      // 復原也是一次存檔,版本清單與差異都要重算。
      setReloadToken((token) => token + 1);
    } catch (error) {
      // 失敗時不關對話框:關掉的話使用者只看到一則提示,卻不知道剛才要復原的是哪一個。
      setRestoreError(messageOf(error, "復原失敗,請稍後再試"));
    } finally {
      setRestoring(false);
    }
  };

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* 背景遮罩不是唯一的關閉路徑,另有 Esc 與關閉鈕。 */}
      <div aria-hidden="true" onClick={onClose} className="absolute inset-0 bg-[rgba(28,25,23,0.42)]" />
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="版本紀錄"
        tabIndex={-1}
        className={
          "relative z-10 flex h-full w-full max-w-2xl flex-col overflow-y-auto border-l " +
          "border-[var(--border)] bg-[var(--bg)] shadow-card"
        }
      >
        <div className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-[var(--border)] bg-[var(--surface)] px-4 py-3">
          <h2 className="m-0 text-base font-semibold text-[var(--text)]">版本紀錄</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="關閉版本紀錄"
            className="rounded-[var(--radius)] p-1 text-[var(--text-3)] hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
          >
            <X aria-hidden="true" size={18} />
          </button>
        </div>

        <div className="flex min-w-0 flex-col gap-3 px-4 py-3">
          {/* 狀態不可以只靠顏色,也不可以只靠 title:把按鈕畫暗而不說話,
              使用者分不出是壞了還是被鎖了。這一行講的是下一步該做什麼。 */}
          {blocked ? (
            <p
              role="status"
              className={
                "m-0 rounded-[var(--radius)] border border-[var(--border-strong)] " +
                "bg-[var(--surface-2)] px-3 py-2 text-sm text-[var(--text)]"
              }
            >
              {blockedReason}。
            </p>
          ) : null}

          <RevisionList
            revisions={revisions}
            loading={listLoading}
            error={listError}
            fromVersion={fromVersion}
            currentVersion={currentVersion}
            onSelect={selectFrom}
          />

          {fromVersion !== null ? (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                size="sm"
                icon={<Undo2 aria-hidden="true" size={14} />}
                disabled={blocked}
                title={blockedReason}
                onClick={() =>
                  // 整張單沒有「新增/刪除」可言:回到那一版的樣子,固定是覆蓋。
                  askRestore({ scope: "QUOTE", uid: null, name: "", change: "CHANGED" })
                }
              >
                復原整張單到版本 {fromVersion}
              </Button>
            </div>
          ) : null}

          {!listLoading && listError === null && revisions.length === 1 ? (
            <p className="m-0 text-sm text-[var(--text-3)]">目前只有一個版本,還沒有可以比較的對象。</p>
          ) : null}

          <DiffView
            diff={diff}
            loading={diffLoading}
            error={diffError}
            // 分類與明細那兩種復原也要一起鎖住 —— 只鎖「整張單」那一顆的話,
            // 同一條寫入路徑照樣從差異清單裡走得出去。
            restoreDisabledReason={blockedReason}
            // `change` 一定要帶過去:復原的後果完全取決於它。被標成「新增」的
            // 分類在舊版根本不存在,復原等於把它連同底下每一列整個刪掉 ——
            // 確認對話框要說得出這件事,不能一律說「內容會被蓋掉」。
            onRestoreSection={(section) =>
              askRestore({
                scope: "SECTION",
                uid: section.uid,
                name: section.title || "(未命名分類)",
                change: section.change,
                lineCount: section.lines.length,
              })
            }
            onRestoreLine={(_section, line) =>
              askRestore({
                scope: "LINE",
                uid: line.uid,
                name: line.name || "(未命名明細)",
                change: line.change,
              })
            }
          />
        </div>

        <RestoreConfirmDialog
          target={target}
          fromVersion={fromVersion}
          pending={restoring}
          error={restoreError}
          onCancel={() => {
            setTarget(null);
            setRestoreError(null);
          }}
          onConfirm={() => void confirmRestore()}
        />
      </aside>
    </div>,
    document.body,
  );
}
